import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  Radio, 
  CardContent, 
  CardActions, 
  Button,
  Link,
  Modal,
  useTheme,
  useMediaQuery
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';

/**
 * ContextColumn component
 * 
 * Displays a list of context cards with radio buttons for selection
 * Each card has a title and a link to details
 * On mobile: Shows as a button that opens a modal
 * On desktop: Shows as a column layout
 * 
 * @param {Object} props Component props
 * @param {Array} props.contextCards Array of context card objects
 * @param {number|null} props.selectedContext ID of the currently selected context
 * @param {Function} props.onContextChange Callback when a context is selected
 * @returns {JSX.Element} Rendered component
 */
const ContextColumn = ({ contextCards, selectedContext, onContextChange }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (contextCards.length > 0 && selectedContext === null) {
      onContextChange(contextCards[0].id);
    }
  }, [contextCards, selectedContext, onContextChange]);

  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);

  const handleContextSelect = (id) => {
    onContextChange(id);
    if (isMobile) {
      handleCloseModal();
    }
  };

  const ContextCards = () => (
    <>
      {contextCards.map((card) => (
        <Card 
          key={card.id} 
          sx={{ 
            mb: 2, 
            position: 'relative', 
            bgcolor: 'background.default',
            borderRadius: 3,
            cursor: 'pointer',
            '&:hover': {
              boxShadow: 3
            }
          }}
          onClick={() => handleContextSelect(card.id)}
        >
          <CardContent>
            <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
              <Radio 
                checked={selectedContext === card.id}
                onChange={() => handleContextSelect(card.id)}
                size="small"
              />
            </Box>
            <Typography variant="h6" sx={{ pr: 4 }}>
              {card.title}
            </Typography>
          </CardContent>
          <CardActions>
            <Button 
              href={card.link}
              onClick={(e) => e.stopPropagation()}
              sx={{
                color: 'primary',
                backgroundColor: 'transparent',
                borderRadius: 2,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText'
                }
              }}
            >
              <Typography variant="body2">
                Ver detalles
              </Typography>
            </Button>
          </CardActions>
        </Card>
      ))}
    </>
  );

  // Mobile: Button that opens modal
  if (isMobile) {
    const selectedCard = contextCards.find(card => card.id === selectedContext);
    
    return (
      <>
        <Button
          variant="outlined"
          startIcon={<SettingsIcon sx={{ marginRight: '10px' }}/>}
          onClick={handleOpenModal}
          sx={{
            border: '2px solid',
            width: '100%',
            justifyContent: 'flex-start',
            textTransform: 'none',
            py: 1.5
          }}
        >
          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="body2" color="text.secondary">
              Contexto
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {selectedCard ? selectedCard.title : 'Seleccionar contexto'}
            </Typography>
          </Box>
        </Button>

        <Modal
          open={modalOpen}
          onClose={handleCloseModal}
          aria-labelledby="context-modal-title"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: 400,
            maxHeight: '80vh',
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: 24,
            p: 3,
            overflowY: 'auto'
          }}>
            <Typography variant="h5" component="h2" sx={{ mb: 3, textAlign: 'center' }}>
              Seleccionar Contexto
            </Typography>
            <ContextCards />
          </Box>
        </Modal>
      </>
    );
  }

  // Desktop: Column layout
  return (
    <Grid container direction="column" sx={{ height: '100%' }}>
      <Grid item sx={{ mb: 2, ml: 2 }}>
        <Typography variant="h4" sx={{ textAlign: 'left', color: 'text.primary' }}>
          Contexto
        </Typography>
      </Grid>
      <Grid item sx={{ overflowY: 'auto', flexGrow: 1 }}>
        <Box sx={{
          bgcolor: 'grey.50',
          borderRadius: 2,
          height: '100%',
          width: '100%',
          p: 2,
          borderRadius: 5,
        }}>
          <ContextCards />
        </Box>
      </Grid>
    </Grid>
  );
};

export default ContextColumn;
